import React from 'react';

import { BulkImportUserSummaryType } from '~/enums/BulkImportUserSummaryType';
import { BulkImportUserToogleActionName } from '~/enums/BulkImportUserToogleActionName';
import { FormattedMessage, useIntl } from 'react-intl';
import Text from '~/ui/components/Text/Text';

/**
 * Hook that returns a builder function for import summary content.
 */
export const useImportSummaryContent = () => {
  const intl = useIntl();

  const buildSummary = (importResults, actionsState) => {
    const { emailNewUsers, deleteMissingUsers, updateExistingUsers } =
      actionsState;

    const newUserDescription = (
      <Text variant="tiny">
        {emailNewUsers ? (
          <FormattedMessage
            id="ImportSummaryContent.newUserDescriptionWithEmail"
            defaultMessage="Users that will be added <strong>and emailed</strong> upon import."
            description="Description for new users section with email notification"
            values={{
              strong: (chunks) => <Text variant="tiny-bold">{chunks}</Text>,
            }}
          />
        ) : (
          <FormattedMessage
            id="ImportSummaryContent.newUserDescription"
            defaultMessage="Users that will be added upon import."
            description="Description for new users section"
          />
        )}
      </Text>
    );

    const existingUserDescription = (
      <Text variant="tiny">
        {updateExistingUsers ? (
          <FormattedMessage
            id="ImportSummaryContent.existingUserDescriptionWithUpdate"
            defaultMessage="Number of users that exist in the uploaded list <strong>will have their information updated</strong> on import."
            description="Description for existing users section with update action"
            values={{
              strong: (chunks) => <Text variant="tiny-bold">{chunks}</Text>,
            }}
          />
        ) : (
          <FormattedMessage
            id="ImportSummaryContent.existingUserDescription"
            defaultMessage="Number of users that exist in the uploaded list."
            description="Description for existing users section"
          />
        )}
      </Text>
    );

    const missingUserDescription = (
      <Text variant="tiny">
        {deleteMissingUsers ? (
          <FormattedMessage
            id="ImportSummaryContent.missingUserDescriptionWithDelete"
            defaultMessage="Users present in your account, but not in the uploaded list. <strong>Users will be removed</strong> from your account on import."
            description="Description for missing users section with delete action"
            values={{
              strong: (chunks) => <Text variant="tiny-bold">{chunks}</Text>,
            }}
          />
        ) : (
          <FormattedMessage
            id="ImportSummaryContent.missingUserDescription"
            defaultMessage="Users present in your account, but not in the uploaded list."
            description="Description for missing users section"
          />
        )}
      </Text>
    );

    return {
      newUsers: {
        resultTypeName: intl.formatMessage({
          id: 'ImportSummaryContent.newUsers',
          defaultMessage: 'New users',
          description: 'Label for new users section',
        }),
        resultTypeDescription: newUserDescription,
        resultTypeValue: importResults[BulkImportUserSummaryType.NewUsers],
        resultTypeActionName: intl.formatMessage({
          id: 'ImportSummaryContent.emailNewUsersAction',
          defaultMessage: 'Email new users sign-in information',
          description: 'Action to email new users',
        }),
        resultTypeAction: BulkImportUserToogleActionName.EmailNewUsers,
      },
      existingUsers: {
        resultTypeName: intl.formatMessage({
          id: 'ImportSummaryContent.existingUsers',
          defaultMessage: 'Existing users',
          description: 'Label for existing users section',
        }),
        resultTypeDescription: existingUserDescription,
        resultTypeValue: importResults[BulkImportUserSummaryType.Existing],
        resultTypeActionName: intl.formatMessage({
          id: 'ImportSummaryContent.updateExistingUsersAction',
          defaultMessage: 'Update existing users with data from upload',
          description: 'Action to update existing users',
        }),
        resultTypeAction: BulkImportUserToogleActionName.UpdateExistingUsers,
      },
      toBeDeletedUsers: {
        resultTypeName: intl.formatMessage({
          id: 'ImportSummaryContent.missingUsers',
          defaultMessage: 'Missing users',
          description: 'Label for missing users section',
        }),
        resultTypeDescription: missingUserDescription,
        resultTypeValue: importResults[BulkImportUserSummaryType.ToBeDeleted],
        resultTypeActionName: intl.formatMessage({
          id: 'ImportSummaryContent.deleteMissingUsersAction',
          defaultMessage: 'Delete missing users',
          description: 'Action to delete missing users',
        }),
        resultTypeAction: BulkImportUserToogleActionName.DeleteMissingUsers,
        isDangerous: true,
      },
    };
  };

  return buildSummary;
};
